<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();

const scheduledMessages = useMapGetter(
  'scheduledMessages/getScheduledMessages'
);
const uiFlags = useMapGetter('scheduledMessages/getUIFlags');

const statusFilter = ref('');
const editDialogRef = ref(null);
const editingMessage = ref(null);
const editContent = ref('');
const editDate = ref('');
const editTime = ref('');
const isUpdating = ref(false);

const statusConfig = {
  pending: {
    label: 'SCHEDULED_MESSAGES.STATUS.PENDING',
    class: 'bg-n-amber-3 text-n-amber-11',
    icon: 'i-lucide-clock',
  },
  sent: {
    label: 'SCHEDULED_MESSAGES.STATUS.SENT',
    class: 'bg-n-teal-3 text-n-teal-11',
    icon: 'i-lucide-check',
  },
  failed: {
    label: 'SCHEDULED_MESSAGES.STATUS.FAILED',
    class: 'bg-n-ruby-3 text-n-ruby-11',
    icon: 'i-lucide-x',
  },
  cancelled: {
    label: 'SCHEDULED_MESSAGES.STATUS.CANCELLED',
    class: 'bg-n-slate-3 text-n-slate-10',
    icon: 'i-lucide-ban',
  },
};

const getStatusConfig = status => {
  return statusConfig[status] || statusConfig.pending;
};

const filteredMessages = computed(() => {
  const messages = scheduledMessages.value || [];
  if (!statusFilter.value) return messages;
  return messages.filter(m => m.status === statusFilter.value);
});

const tableHeaders = computed(() => [
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONTACT'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.INBOX'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONVERSATION'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.CONTENT'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.SCHEDULED_AT'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.STATUS'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.AGENT'),
  t('SCHEDULED_MESSAGES.ADMIN.TABLE.ACTIONS'),
]);

const formatDateTime = dateStr => {
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const truncateContent = (content, maxLength = 60) => {
  if (!content) return '';
  return content.length > maxLength
    ? content.substring(0, maxLength) + '...'
    : content;
};

const today = computed(() => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
});

const editScheduledDateTime = computed(() => {
  if (!editDate.value || !editTime.value) return null;
  return new Date(`${editDate.value}T${editTime.value}`);
});

const isEditFormValid = computed(() => {
  if (!editContent.value.trim()) return false;
  if (!editScheduledDateTime.value) return false;
  if (editScheduledDateTime.value <= new Date()) return false;
  return true;
});

const openEditDialog = msg => {
  editingMessage.value = msg;
  editContent.value = msg.content || '';
  const dt = new Date(msg.scheduled_at);
  editDate.value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  editTime.value = `${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
  editDialogRef.value?.open();
};

const closeEditDialog = () => {
  editingMessage.value = null;
  editContent.value = '';
  editDate.value = '';
  editTime.value = '';
  editDialogRef.value?.close();
};

const saveEdit = async () => {
  if (!editingMessage.value || !isEditFormValid.value) return;
  isUpdating.value = true;
  try {
    await store.dispatch('scheduledMessages/update', {
      id: editingMessage.value.id,
      scheduled_message: {
        content: editContent.value.trim(),
        scheduled_at: editScheduledDateTime.value.toISOString(),
        conversation_id: editingMessage.value.conversation_id,
        inbox_id: editingMessage.value.inbox_id,
      },
    });
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.UPDATED'));
    closeEditDialog();
  } catch {
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

const cancelMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/cancel', message.id);
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.CANCELLED'));
  } catch {
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.CANCEL_ERROR'));
  }
};

const deleteMessage = async message => {
  try {
    await store.dispatch('scheduledMessages/delete', message.id);
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.DELETED'));
  } catch {
    useAlert(t('SCHEDULED_MESSAGES.ALERTS.DELETE_ERROR'));
  }
};

const filterStatuses = [
  { key: '', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.ALL' },
  { key: 'pending', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.PENDING' },
  { key: 'sent', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.SENT' },
  { key: 'failed', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.FAILED' },
  { key: 'cancelled', label: 'SCHEDULED_MESSAGES.ADMIN.FILTER.CANCELLED' },
];

onMounted(() => {
  store.dispatch('scheduledMessages/get');
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('SCHEDULED_MESSAGES.ADMIN.TITLE')"
    :description="$t('SCHEDULED_MESSAGES.ADMIN.DESCRIPTION')"
  >
    <template #actions>
      <div class="flex items-center gap-1">
        <Button
          v-for="filter in filterStatuses"
          :key="filter.key"
          xs
          :color="statusFilter === filter.key ? 'blue' : 'slate'"
          :variant="statusFilter === filter.key ? 'solid' : 'faded'"
          :label="$t(filter.label)"
          @click="statusFilter = filter.key"
        />
      </div>
    </template>

    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.isFetching"
      :is-empty="!filteredMessages.length"
      :empty-message="$t('SCHEDULED_MESSAGES.ADMIN.EMPTY')"
    >
      <tr
        v-for="msg in filteredMessages"
        :key="msg.id"
        class="transition-colors hover:bg-n-solid-3"
      >
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-12">
            {{ msg.contact_name || '-' }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-11">
            {{ msg.inbox_name || '-' }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-12">
            #{{ msg.display_id || msg.conversation_id }}
          </span>
        </td>
        <td class="px-5 py-3">
          <span
            class="text-sm text-n-slate-12"
            :title="msg.content"
          >
            {{ truncateContent(msg.content) }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-11">
            {{ formatDateTime(msg.scheduled_at) }}
          </span>
        </td>
        <td class="px-5 py-3">
          <span
            class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-md"
            :class="getStatusConfig(msg.status).class"
          >
            <span
              :class="getStatusConfig(msg.status).icon"
              class="size-3"
            />
            {{ $t(getStatusConfig(msg.status).label) }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-11">
            {{ msg.user?.name || '-' }}
          </span>
        </td>
        <td class="px-5 py-3">
          <div class="flex items-center gap-1">
            <Button
              v-if="msg.status === 'pending'"
              xs
              slate
              faded
              icon="i-lucide-pencil"
              :label="$t('SCHEDULED_MESSAGES.ACTIONS.EDIT')"
              @click="openEditDialog(msg)"
            />
            <Button
              v-if="msg.status === 'pending'"
              xs
              ruby
              faded
              icon="i-lucide-ban"
              :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
              @click="cancelMessage(msg)"
            />
            <Button
              v-if="msg.status === 'cancelled' || msg.status === 'failed'"
              xs
              slate
              faded
              icon="i-lucide-trash-2"
              :label="$t('SCHEDULED_MESSAGES.ACTIONS.DELETE')"
              @click="deleteMessage(msg)"
            />
          </div>
        </td>
      </tr>
    </SettingsTable>
  </SettingsPageLayout>

  <!-- Edit Dialog -->
  <Dialog
    ref="editDialogRef"
    :title="$t('SCHEDULED_MESSAGES.EDIT_DIALOG.TITLE')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="closeEditDialog"
  >
    <div class="flex flex-col gap-4">
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_LABEL') }}
        </label>
        <textarea
          v-model="editContent"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand resize-none"
          rows="4"
          :placeholder="$t('SCHEDULED_MESSAGES.DIALOG.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div class="flex gap-3">
        <div class="flex flex-col flex-1 gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('SCHEDULED_MESSAGES.DIALOG.DATE_LABEL') }}
          </label>
          <input
            v-model="editDate"
            type="date"
            :min="today"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          />
        </div>
        <div class="flex flex-col flex-1 gap-1.5">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('SCHEDULED_MESSAGES.DIALOG.TIME_LABEL') }}
          </label>
          <input
            v-model="editTime"
            type="time"
            class="w-full px-3 py-2 text-sm border rounded-lg bg-n-solid-2 border-n-weak text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand focus:border-n-brand"
          />
        </div>
      </div>

      <div class="flex items-center justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('SCHEDULED_MESSAGES.ACTIONS.CANCEL')"
          @click="closeEditDialog"
        />
        <Button
          blue
          icon="i-lucide-check"
          :label="$t('SCHEDULED_MESSAGES.ACTIONS.UPDATE')"
          :disabled="!isEditFormValid || isUpdating"
          :is-loading="isUpdating"
          @click="saveEdit"
        />
      </div>
    </div>
  </Dialog>
</template>
